import React, { Suspense } from "react";
import { resolveIcon, IconKey } from "../constants/iconMap";

// Lazy + deep import: keeps lucide's ~1800-entry icon import-map out of the main
// bundle. It loads as a separate async chunk only when a Lucide icon actually
// renders — emoji / image-URL / key portlets stay zero-cost.
const DynamicIcon = React.lazy(() => import("lucide-react/dist/esm/DynamicIcon"));

// Structural shape so this accepts every portlet icon variant in the app
// (PortletConfigService and PortletService declare slightly different unions).
type IconShape = { type?: string; value?: string; key?: string; color?: string };
type IconLike = IconShape | string | undefined | null;

// Lower-case Lucide icon name, e.g. "settings-2", "shield-question-mark", "link".
// Lowercase-only + no slash/dot so it can't match an emoji (non-ASCII), a URL/path,
// or an UPPER_SNAKE ICON_MAP key. Used to recover Lucide icons saved with the wrong
// `type` (or as a bare string). Returns the normalised name, or null if not a match.
const asLucideName = (raw: string | undefined): string | null => {
  if (!raw) return null;
  const s = raw.trim();
  return /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/.test(s) ? s : null;
};

const imgStyle = (size: number): React.CSSProperties => ({
  maxHeight: size,
  maxWidth: size,
  objectFit: "contain",
});

const hideOnError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  (e.currentTarget as HTMLImageElement).style.display = "none";
};

const renderLucide = (name: string, size: number, className?: string, color?: string) => (
  <Suspense fallback={<span className={className} style={{ display: "inline-block", width: size, height: size }} />}>
    <DynamicIcon name={name as any} size={size} className={className} color={color || undefined} />
  </Suspense>
);

interface Props {
  icon: IconLike;
  /** px — applies to lucide + image. Emoji uses the inherited container font-size. */
  size?: number;
  className?: string;
}

/**
 * Renders any portlet icon shape (emoji / image URL / icon key / lucide) to a node.
 * Use this anywhere a portlet icon is displayed so all four icon types render uniformly.
 */
const IconDisplay: React.FC<Props> = ({ icon, size = 24, className }) => {
  // Legacy iLearn shape: a bare string is an image URL / asset path — unless it's
  // actually a Lucide icon name saved without the wrapper object.
  if (typeof icon === "string") {
    if (!icon) return <span className={className}>📄</span>;
    const lucideName = asLucideName(icon);
    if (lucideName) return renderLucide(lucideName, size, className);
    return <img src={icon} alt="" className={className} style={imgStyle(size)} onError={hideOnError} />;
  }
  if (!icon) return <span className={className}>📄</span>;

  // Render as Lucide when the type says so, OR when the value is a Lucide name but
  // the type was mis-saved as emoji/key (common with the icon-library picker).
  const lucideName =
    icon.type !== "url" && icon.type !== "image" && !icon.key ? asLucideName(icon.value) : null;
  if (icon.type === "lucide" && icon.value) {
    return renderLucide(icon.value.trim(), size, className, icon.color);
  }
  if (lucideName) {
    return renderLucide(lucideName, size, className, icon.color);
  }

  if ((icon.type === "url" || icon.type === "image") && icon.value) {
    return <img src={icon.value} alt="" className={className} style={imgStyle(size)} onError={hideOnError} />;
  }

  // emoji or key → resolveIcon returns an emoji string
  return (
    <span className={className}>
      {resolveIcon(icon as { type: string; value?: string; key?: IconKey })}
    </span>
  );
};

export default IconDisplay;
